import React, { useCallback, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { Container, Row, Col, Icon, Div } from '@pinpoint/design-kit';

import { Message, Button } from 'src/components';
import EqualBucketsSelector from 'src/components/models/equal_buckets_selector';
import { modelViewActions } from 'src/store/actions';
import { Page, Rbac } from 'src/containers';
import CONSTANTS from 'src/store/constants';
import { ROUTES } from 'src/config';
import LiftCharts from 'src/components/models/charts/lift_charts';
import { queryHelper } from 'src/functions';
import { ApproveLiftChartDownloadDialog } from 'src/components/dialogs';

function ModelsLiftCharts({
  history,
  location: { search, pathname },
  match: {
    params: { modelClass, modelId },
  },
  modelView,
  modelView: { equalType },
  fetchModel,
  setEqualType,
  setQuantiles,
  setChart,
  setGradient,
  resetPage,
  liftChartDownloadUrl,
  fetchLiftChartDownloadUrl,
}) {
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const openApproveModal = useCallback(() => setApproveModalOpen(true), [setApproveModalOpen]);
  const closeApproveModal = useCallback(() => setApproveModalOpen(false), [setApproveModalOpen]);
  const handleApprove = useCallback(() => window.open(liftChartDownloadUrl, '_blank'), [liftChartDownloadUrl]);

  const [altChart, setAltChart] = useState(false);
  const modelVersion = useMemo(() => modelView?.model?.payload?.version, [modelView]);

  const getData = useCallback(() => {
    if (modelId) {
      fetchModel(modelId);
      fetchLiftChartDownloadUrl(modelId, 'segment');
    }
  }, [modelId, fetchModel, fetchLiftChartDownloadUrl]);

  // When the tenant or the model id changes, retrieve the model anew
  useEffect(() => {
    getData();
  }, [getData]);

  // When there's new query params parse for chart, redirect to lift-charts if not explicitly set
  useEffect(() => {
    queryHelper(history, (query) => {
      // When there's new query params parse for ref, if it's present set alt chart
      const nextRef = query.get('ref');
      if (nextRef !== null) {
        setAltChart(true);
        query.delete('ref');
      }
    });
  }, [history, search, pathname, modelVersion, setAltChart]);

  useEffect(() => resetPage, [resetPage]);

  return (
    <Container
      w="100%"
      minW="100%"
      h="100%"
      minH="100%"
      data-debug="container"
      d="flex"
      flexDir="column"
      overflow="hidden"
    >
      <Row data-debug="nav-row" p={{ t: '1.5rem' }} m={{ x: '.5rem' }}>
        <Col gutterWidth="0" d="inline-flex" size="12" align="center">
          <Button
            buttonStyle="primary"
            onClick={() => {
              history.push(ROUTES.MODELS);
            }}
          >
            <Icon name="LongLeft" color="white" m={{ r: '0.5rem' }} size="1rem" />
            BACK
          </Button>
          <Div data-test="roi-calculator-btn">
            <Button
              buttonStyle="primary"
              data-tip="View financial impact model"
              onClick={() => {
                history.push(
                  ROUTES.MODEL_ROI_CALCULATOR.replace(':modelId', modelId).replace(':modelClass', modelClass)
                );
              }}
            >
              <Icon name="Roi" viewBox="0 0 16 16" color="white" m={{ r: '0.5rem' }} size="1rem" />
              ROI CALCULATOR
            </Button>
          </Div>
          <Rbac roles={[CONSTANTS.ROLE.SUPER_ADMIN, CONSTANTS.ROLE.CLIENT_SERVICES_ADMIN]}>
            <Div data-test="download-btn-div">
              <Button
                buttonStyle="admin"
                data-test="download-btn"
                data-tip={[null, undefined].includes(liftChartDownloadUrl) ? null : 'Download predictions and actuals'}
                disabled={[null, undefined].includes(liftChartDownloadUrl)}
                onClick={openApproveModal}
              >
                <Icon name="Download2" color="white" size="1rem" viewBox="0 0 14 14" m={{ r: '.5rem' }} />
                DOWNLOAD
              </Button>
            </Div>
            <ApproveLiftChartDownloadDialog
              isOpen={approveModalOpen}
              actions={{
                close: {
                  on: closeApproveModal,
                },
                cancel: {
                  on: () => {},
                },
                handlers: [
                  {
                    on: handleApprove,
                  },
                ],
              }}
            />
          </Rbac>
          <EqualBucketsSelector
            models={[modelView?.model]}
            equalType={equalType}
            handleEqualTypeChange={setEqualType}
          />
        </Col>
      </Row>
      <Row data-debug="content" p={{ t: '.5rem' }} m={{ x: '.5rem' }} h="100%" justify="stretch" align="stretch">
        {
          {
            [CONSTANTS.API.SUCCEEDED]: (
              <Col
                gutterWidth="0"
                size="12"
                bg="white"
                rounded="md"
                overflow="hidden"
                d="flex"
                flexDir="column"
                data-test="paper"
              >
                <LiftCharts
                  altChart={altChart}
                  equalType={equalType}
                  modelView={modelView}
                  setChart={setChart}
                  setQuantiles={setQuantiles}
                  setGradient={setGradient}
                />
              </Col>
            ),
            [CONSTANTS.API.FAILED]: <Message type="error" />,
            [CONSTANTS.API.STARTED]: <Message />,
            default: <Message default type="info" text="No data" cta={{ text: 'Refresh', onClick: getData }} />,
          }[modelView.fetchModelStatus]
        }
      </Row>
    </Container>
  );
}

const mapStateToProps = ({ app, modelView, modelView: { liftChartDownloadUrl } }, ownProps) => ({
  app,
  modelView,
  liftChartDownloadUrl,
  ...ownProps,
});

const mapDispatchToProps = (dispatch) => ({
  fetchModel: (...params) => dispatch(modelViewActions.fetchModel(...params)),
  setChart: (...params) => dispatch(modelViewActions.setChart(...params)),
  setGradient: (...params) => dispatch(modelViewActions.setGradient(...params)),
  setQuantiles: (...params) => dispatch(modelViewActions.setQuantiles(...params)),
  setEqualType: (...params) => dispatch(modelViewActions.setEqualType(...params)),
  unset: (key) => dispatch(modelViewActions.unset(key)),
  resetPage: (key) => dispatch(modelViewActions.resetPage()),
  fetchLiftChartDownloadUrl: (entityId, entityType) =>
    dispatch(modelViewActions.fetchLiftChartDownloadUrl(entityId, entityType)),
});

ModelsLiftCharts.propTypes = {
  history: PropTypes.shape({ push: PropTypes.func.isRequired }).isRequired,
  location: PropTypes.shape({ search: PropTypes.string.isRequired, pathname: PropTypes.string.isRequired }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({ modelClass: PropTypes.string.isRequired, modelId: PropTypes.string.isRequired }),
  }).isRequired,
  app: PropTypes.shape({
    profile: PropTypes.shape({
      tenant: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
    }),
  }).isRequired,
  modelView: PropTypes.shape({
    model: PropTypes.shape({
      payload: PropTypes.shape({
        version: PropTypes.string.isRequired,
      }).isRequired,
      modelData: PropTypes.shape({
        dataPayload: PropTypes.shape({
          equal_premiums: PropTypes.oneOf([undefined, PropTypes.shape({})]).isRequired,
          equal_persons: PropTypes.oneOf([undefined, PropTypes.shape({})]).isRequired,
        }).isRequired,
      }).isRequired,
      dataModelType: PropTypes.string.isRequired,
    }),
    availableQuantiles: PropTypes.shape({
      current: PropTypes.arrayOf(PropTypes.number).isRequired,
      premiums: PropTypes.arrayOf(PropTypes.number),
      persons: PropTypes.arrayOf(PropTypes.number),
    }).isRequired,
    equalType: PropTypes.oneOf([PropTypes.string, undefined]).isRequired,
    fetchModelStatus: PropTypes.oneOf([PropTypes.string, undefined]).isRequired,
  }).isRequired,
  fetchModel: PropTypes.func.isRequired,
  setEqualType: PropTypes.func.isRequired,
  setQuantiles: PropTypes.func.isRequired,
  setChart: PropTypes.func.isRequired,
  setGradient: PropTypes.func.isRequired,
  resetPage: PropTypes.func.isRequired,
  liftChartDownloadUrl: PropTypes.string.isRequired,
  fetchLiftChartDownloadUrl: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Page(ModelsLiftCharts));
